/**
 * Carga de un caso: motor + pack de contenido + capa visual, todo en un objeto.
 *
 * **Alcance deliberadamente corto.** El caso diario, el selector de dificultad y el
 * progreso guardado son la etapa 4 (MON-61); aquí solo hay lo mínimo para que la
 * pantalla de juego tenga algo que pintar y para que el e2e pueda pedir un caso
 * concreto: `?n=4&semilla=…&tema=casa-senorial`. Cuando llegue MON-61, esto se sustituye
 * por su cargador sin tocar `CasePage` ni la rejilla.
 */

import { useMemo } from 'react';
import type { Case } from '@murdoku/engine';
import { generateCase } from '@murdoku/engine';
import type { RenderedCase, RulesText, ThemePack } from '@murdoku/content';
import { getPack, PACKS, renderCase, rulesFor, sampleTheme } from '@murdoku/content';

import type { PersonVisual, RoomVisual } from './visuals';
import { personVisuals, roomVisuals } from './visuals';

export interface CaseSetup {
  n: number;
  seed: string;
  packId: string;
}

export const DEFAULT_SETUP: CaseSetup = {
  n: 4,
  seed: 'muestra-1',
  packId: 'casa-senorial',
};

/** Rango admitido: por debajo de 4 el caso no tiene gracia; por encima de 8 no hay pack. */
const MIN_N = 4;
const MAX_N = 8;

export function setupFromSearch(search: string): CaseSetup {
  const params = new URLSearchParams(search);

  const rawN = Number(params.get('n'));
  const n =
    Number.isInteger(rawN) && rawN >= MIN_N && rawN <= MAX_N ? rawN : DEFAULT_SETUP.n;

  const seed = params.get('semilla')?.trim();
  const packId = params.get('tema');

  return {
    n,
    seed: seed !== undefined && seed !== '' ? seed : DEFAULT_SETUP.seed,
    packId:
      packId !== null && PACKS.some((pack) => pack.id === packId)
        ? packId
        : DEFAULT_SETUP.packId,
  };
}

export interface LoadedCase {
  setup: CaseSetup;
  caso: Case;
  pack: ThemePack;
  text: RenderedCase;
  rules: RulesText;
  rooms: RoomVisual[];
  people: PersonVisual[];
  /** Descriptor breve por nombre («la médica del pueblo»), para el panel del reparto. */
  descriptors: ReadonlyMap<string, string>;
}

export function loadCase(setup: CaseSetup): LoadedCase {
  const pack = getPack(setup.packId);
  const caso = generateCase({
    n: setup.n,
    seed: setup.seed,
    theme: sampleTheme(pack, setup.seed),
  });
  const peopleInPack = pack.people.map((person) => person.name);

  return {
    setup,
    caso,
    pack,
    text: renderCase(caso, pack),
    rules: rulesFor(pack),
    rooms: roomVisuals(caso.rooms),
    people: personVisuals(caso.people, peopleInPack),
    descriptors: new Map(pack.people.map((one) => [one.name, one.descriptor])),
  };
}

/** Identificador estable del caso: sirve de `key` para reiniciar el estado al cambiar. */
export function caseKey(setup: CaseSetup): string {
  return `${setup.packId}:${setup.n}:${setup.seed}`;
}

export function useCase(search: string): LoadedCase {
  return useMemo(() => loadCase(setupFromSearch(search)), [search]);
}
